import type { Uuid } from "@/domains/global/identifiers";
import type { SyncModelScope } from "@/domains/sync-scopes/types";
import { resolveAssistantTaskNoteReferenceSyncModelUuid } from "@/modules/uuid/sync-models/resolve-assistant-task-note-reference-sync-model-uuid";
import type { AssistantTaskNoteReferenceModelData } from "@/store/assistant/types";
import type { OptimisticSyncUpdate, SyncUpdateValue } from "@/store/sync/types";

export const buildAssistantTaskNoteReferenceOptimisticUpsert = ({
  assistantId,
  committedAt,
  modelScopes,
  noteId,
  optimisticUpdateId,
  spaceAccountId,
  taskId,
}: {
  assistantId: Uuid;
  committedAt: string;
  modelScopes: SyncModelScope[];
  noteId: Uuid;
  optimisticUpdateId: Uuid;
  spaceAccountId: Uuid;
  taskId: Uuid;
}): OptimisticSyncUpdate<AssistantTaskNoteReferenceModelData> => {
  const value = {
    model_id: resolveAssistantTaskNoteReferenceSyncModelUuid({ taskId, noteId }),
    model_kind: "ASSISTANT_TASK_NOTE_REFERENCE",
    model_version: 1,
    model_data: {
      space_account_id: spaceAccountId,
      assistant_id: assistantId,
      task_id: taskId,
      note_id: noteId,
      created_at: committedAt,
      updated_at: committedAt,
    },
    model_scopes: modelScopes,
  } satisfies SyncUpdateValue<AssistantTaskNoteReferenceModelData>;

  return {
    optimistic_update_id: optimisticUpdateId,
    locally_committed_at: committedAt,
    kind: "UPSERTED",
    value,
  } satisfies OptimisticSyncUpdate<AssistantTaskNoteReferenceModelData>;
};

export const buildAssistantTaskNoteReferenceOptimisticDelete = ({
  committedAt,
  modelId,
  modelVersion,
  optimisticUpdateId,
}: {
  committedAt: string;
  modelId: Uuid;
  modelVersion: number;
  optimisticUpdateId: Uuid;
}): OptimisticSyncUpdate<AssistantTaskNoteReferenceModelData> => {
  return {
    optimistic_update_id: optimisticUpdateId,
    locally_committed_at: committedAt,
    kind: "DELETED",
    value: {
      model_id: modelId,
      model_kind: "ASSISTANT_TASK_NOTE_REFERENCE",
      model_version: modelVersion,
    },
  } satisfies OptimisticSyncUpdate<AssistantTaskNoteReferenceModelData>;
};
